import { IPendingSwap } from '@nkr/models/index';
import { SheetColumn } from '@nkr/utils/exportToExcel';
import { Risk } from '@nkr/views/expert-system/models/risk';

export enum PendingSwapType {
    Patient,
    Donor,
    DonorYourCenter,
    DonorOtherCenters,
    Internal
};

export enum SwapReportType {
    PendingSwaps,
    PastSwaps
};

const commonSwapColumns: SheetColumn[] = [
    { key: 'swapManager', header: 'Swap Manager' },
    { key: 'swapStatus', header: 'Swap Status' },
    { key: 'swapNumber', header: 'Swap #' },
    { key: 'rType', header: 'R Type' },
    { key: 'donorCenter', header: 'Donor Center' },
    { key: 'donorAlias', header: 'Donor Alias' },
    { key: 'patientCenter', header: 'Patient Center' },
    { key: 'patientAlias', header: 'Patient Alias' },
    { key: 'logisticsAndKickoffCalls', header: 'Logistics & Kickoff Calls' },
    { key: 'tubesForTransport', header: 'Tubes for Transport' },
    { key: 'logisticsGps', header: 'Logistics / GPS' },
    { key: 'scheduledDelivery', header: 'Scheduled Delivery' },
];

const patientExclusiveColumns: SheetColumn[] = [
    { key: 'swapManager', header: 'Swap Manager' },
    { key: 'swapStatus', header: 'Swap Status' },
    { key: 'swapNumber', header: 'Swap #' },
    { key: 'rType', header: 'R Type' },
    { key: 'donorCenter', header: 'Donor Center' },
    { key: 'donorAlias', header: 'Donor Alias' },
    { key: 'patientCenter', header: 'Patient Center' },
    { key: 'patientAlias', header: 'Patient Alias' },
    { key: 'epletMmRisk', header: 'Eplet MM Risk' },
    { key: 'alleleMatch', header: 'Allele Match' },
    { key: 'logisticsAndKickoffCalls', header: 'Logistics & Kickoff Calls' },
    { key: 'tubesForTransport', header: 'Tubes for Transport' },
    { key: 'logisticsGps', header: 'Logistics / GPS' },
    { key: 'scheduledDelivery', header: 'Scheduled Delivery' },
];

// Past Swaps columns
export const pastSwapsPatientColumns: SheetColumn[] = [
    ...patientExclusiveColumns,
    { key: 'actualDelivery', header: 'Actual Delivery'}
];

export const pastSwapsColumns: SheetColumn[] = [
    ...commonSwapColumns
];

export const pastSwapsInternalColumns: SheetColumn[] = [
    { key: 'swapManager', header: 'Swap Manager' },
    { key: 'swapStatus', header: 'Swap Status' },
    { key: 'swapNumber', header: 'Swap #' },
    { key: 'donor', header: 'Donor' },
    { key: 'patient', header: 'Patient' },
    { key: 'transplantDate', header: 'Transplant Date' },
    { key: 'tubesForTransport', header: 'Tubes for Trasport' },
];

// Pending Swap columns
export const pendingSwapsPatientColumns: SheetColumn[] = [
    ...patientExclusiveColumns,
    { key: 'deadline', header: 'Deadline' }
];

export const pendingSwapsColumns: SheetColumn[] = [
    ...commonSwapColumns,
    { key: 'deadline', header: 'Deadline' }
];

export const pendingSwapsInternalColumns: SheetColumn[] = [
    ...pastSwapsInternalColumns,
    { key: 'deadline', header: 'Deadline' },
];

export const flattenSwapData = (swapData: IPendingSwap[], centerId: number): unknown[] => {
    const flatData: unknown[] = swapData.map((s) => ({
        swapManager: s.swapManager != null ? s.swapManager.name + '\n' + s.swapManager.phoneNumber + '\n' + s.swapManager.email : '',
        swapStatus: s.swapStatus,
        swapNumber: s.chainId + '/' + s.clusterNumber + '/' + s.positionNumber,
        tubesForTransport: s.tubes.join('\n'),
        rType: s.itemType,
        donorCenter: s.donor.center,
        donorAlias: s.donor.alias,
        patientCenter: s.recipient.centerAlias,
        patientAlias: s.recipient.alias,
        epletMmRisk: translateEpletMissMatchRisk(s.singleEpletMmRisk?.singleEpletRisk),
        alleleMatch: s.bmtMm == null ? 'N/A' : Math.abs(s.bmtMm) + '/8',
        logisticsAndKickoffCalls: getLogisticsAndKickoffCallsDate(s.logisticsCallDateTime, s.kickoffCallDateTime),
        logisticsGps: '',
        scheduledDelivery: s.recipient.kidneyArrivalDateTime,
        deadline: s.recipient.centerId == centerId && s.deadline != '' ? s.deadlineLocal : 'N/A',
        actualDelivery: s.logistics.kidneyArrivalDateTimeActual,
        donor: s.donor.alias,
        patient: s.recipient.alias,
        transplantDate: s.logistics.kidneyArrivalDate,
    }));

    return flatData;
};

export const getColumnDefinition = (swapType: PendingSwapType, swapReportType: SwapReportType): SheetColumn[] =>{
    if (swapReportType == SwapReportType.PendingSwaps)  {
        if (swapType == PendingSwapType.Internal) {
            return pendingSwapsInternalColumns;
        }

        if (swapType == PendingSwapType.Patient) {
            return pendingSwapsPatientColumns;            
        }

        return pendingSwapsColumns;
    }

    if (swapReportType == SwapReportType.PastSwaps) {
        if (swapType == PendingSwapType.Internal) {
            return pastSwapsInternalColumns;
        } 
        
        if (swapType == PendingSwapType.Patient) {
            return pastSwapsPatientColumns;
        }

        return pastSwapsColumns;
    }
};

const getLogisticsAndKickoffCallsDate = (logisticsCallDateTime: string, kickoffCallDateTime: string) => {            
        if (logisticsCallDateTime && kickoffCallDateTime) {
            return logisticsCallDateTime + '\n' + kickoffCallDateTime;
        }

        if (logisticsCallDateTime && !kickoffCallDateTime) {
            return logisticsCallDateTime;
        }

        if (!logisticsCallDateTime && kickoffCallDateTime) {
            return kickoffCallDateTime;
        }
};

const translateEpletMissMatchRisk = (singleEpletRisk: number): string => {
    if (singleEpletRisk == Risk.Zero) {
        return '0 MM';
    } else if (singleEpletRisk == Risk.Low) {
        return 'Low';
    } else if (singleEpletRisk == Risk.Intermediate) {
        return 'Medium';
    } else if (singleEpletRisk == Risk.High) {
        return 'High';
    }

    return '- ? -';
};